import React, { useEffect, useRef, useState } from "react";
import { ActionIcon, Blockquote, Box, Button, Card, Group, Stack, Text } from "@mantine/core";
import BasePage from "./BasePage";
import { IconQuote, IconSunHigh } from "@tabler/icons-react";
import quotesData from '../assets/qotd.json';
import SelectionBox from "../components/SelectionBox";

function SnakeGame() {
    const GRID_SIZE = 30;
    const TILE_COUNT = 20;
    const INITIAL_SNAKE = [{ x: 10, y: 10 }];
    const INITIAL_FOOD = { x: 5, y: 5 };
    const INITIAL_DIRECTION = { x: 0, y: 0 };

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [snake, setSnake] = useState(INITIAL_SNAKE);
    const [food, setFood] = useState(INITIAL_FOOD);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);

    const previousSnakeRef = useRef(INITIAL_SNAKE);
    const lastMoveTimeRef = useRef(performance.now());
    const directionRef = useRef(INITIAL_DIRECTION);
    const queuedDirectionRef = useRef(INITIAL_DIRECTION);

    const MOVE_INTERVAL = 120;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            let nextDirection: { x: number; y: number } | null = null;

            switch (e.key.toLowerCase()) {
                case 'w':
                case 'arrowup':
                    nextDirection = { x: 0, y: -1 };
                    break;
                case 's':
                case 'arrowdown':
                    nextDirection = { x: 0, y: 1 };
                    break;
                case 'a':
                case 'arrowleft':
                    nextDirection = { x: -1, y: 0 };
                    break;
                case 'd':
                case 'arrowright':
                    nextDirection = { x: 1, y: 0 };
                    break;
                default:
                    return;
            }

            e.preventDefault();
            if (!nextDirection) return;

            const current = queuedDirectionRef.current;

            const isSame =
                current.x === nextDirection.x && current.y === nextDirection.y;

            const isReverse =
                current.x === -nextDirection.x && current.y === -nextDirection.y;

            if (isSame || isReverse) return;

            queuedDirectionRef.current = nextDirection;
        };

        window.addEventListener('keydown', handleKeyDown, { passive: false });
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (gameOver) return;

        const interval = setInterval(() => {
            setSnake((prevSnake) => {
                const nextDir = queuedDirectionRef.current;

                if (nextDir.x === 0 && nextDir.y === 0) return prevSnake;

                directionRef.current = nextDir;
                previousSnakeRef.current = prevSnake;
                lastMoveTimeRef.current = performance.now();

                const head = prevSnake[0];
                const newHead = {
                    x: head.x + directionRef.current.x,
                    y: head.y + directionRef.current.y,
                };

                if (
                    newHead.x < 0 ||
                    newHead.y < 0 ||
                    newHead.x >= TILE_COUNT ||
                    newHead.y >= TILE_COUNT ||
                    prevSnake.some(
                        (segment) => segment.x === newHead.x && segment.y === newHead.y
                    )
                ) {
                    setGameOver(true);
                    return prevSnake;
                }

                const ateFood = newHead.x === food.x && newHead.y === food.y;
                const newSnake = [newHead, ...prevSnake];

                if (ateFood) {
                    setScore((s) => s + 1);

                    let newFood;
                    do {
                        newFood = {
                            x: Math.floor(Math.random() * TILE_COUNT),
                            y: Math.floor(Math.random() * TILE_COUNT),
                        };
                    } while (
                        newSnake.some(
                            (segment) =>
                                segment.x === newFood.x && segment.y === newFood.y
                        )
                    );

                    setFood(newFood);
                    return newSnake;
                }

                newSnake.pop();
                return newSnake;
            });
        }, MOVE_INTERVAL);

        return () => clearInterval(interval);
    }, [food, gameOver]);

    useEffect(() => {
        let animationFrameId: number;

        const render = () => {
            const canvas = canvasRef.current;
            if (!canvas) {
                animationFrameId = requestAnimationFrame(render);
                return;
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                animationFrameId = requestAnimationFrame(render);
                return;
            }

            const progress = Math.min(
                (performance.now() - lastMoveTimeRef.current) / MOVE_INTERVAL,
                1
            );

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = '#111';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // FOOD (circle)
            ctx.fillStyle = 'whitesmoke';

            // slightly bigger so segments visually connect
            const radius = (GRID_SIZE - 2) / 2;

            ctx.beginPath();
            ctx.arc(
                food.x * GRID_SIZE + GRID_SIZE / 2,
                food.y * GRID_SIZE + GRID_SIZE / 2,
                radius,
                0,
                Math.PI * 2
            );
            ctx.fill();


            // SNAKE (circles)
            ctx.fillStyle = 'cyan';

            snake.forEach((segment, index) => {
                const prev = previousSnakeRef.current[index] ?? segment;

                const x = prev.x + (segment.x - prev.x) * progress;
                const y = prev.y + (segment.y - prev.y) * progress;

                ctx.beginPath();
                ctx.arc(
                    x * GRID_SIZE + GRID_SIZE / 2,
                    y * GRID_SIZE + GRID_SIZE / 2,
                    radius,
                    0,
                    Math.PI * 2
                );
                ctx.fill();
            });

            animationFrameId = requestAnimationFrame(render);
        };

        animationFrameId = requestAnimationFrame(render);
        return () => cancelAnimationFrame(animationFrameId);
    }, [snake, food]);

    const resetGame = () => {
        setSnake(INITIAL_SNAKE);
        setFood(INITIAL_FOOD);
        setGameOver(false);
        setScore(0);
        directionRef.current = INITIAL_DIRECTION;
        queuedDirectionRef.current = INITIAL_DIRECTION;
        previousSnakeRef.current = INITIAL_SNAKE;
        lastMoveTimeRef.current = performance.now();
        window.location.reload();
    };

    return (
        <Stack>
            <Text ta="center" fz="1rem">{score}</Text>

            <canvas
                ref={canvasRef}
                width={GRID_SIZE * TILE_COUNT}
                height={GRID_SIZE * TILE_COUNT}
            />

            {gameOver ? (
                <Button className="unify-btn-primary" onClick={resetGame}>
                    Play Again
                </Button>
            ) : (
                <Text ta="center" fz="1rem">
                    Use WASD or arrow keys to begin.
                </Text>
            )}
        </Stack>
    );
}

function DashboardPage() {
    const [quote, setQuote] = useState<string>();
    const [author, setAuthor] = useState<string>();

    const selectRandomQuote = () => {
        const randomQuote = quotesData.quotes[Math.floor(Math.random() * quotesData.quotes.length)];
        setQuote(randomQuote.text);
        setAuthor(randomQuote.author);
    };

    useEffect(() => {
        selectRandomQuote();
    }, [])

    return (
        <BasePage fullWidth>
            {process.env.NODE_ENV === 'development' ?
                <Group>
                    <Button>Default Button</Button>
                    <Button disabled>Default Button Disabled</Button>
                    <Button className="unify-btn-primary">Primary Button</Button>
                    <Button disabled className="unify-btn-primary">Primary Button Disabled</Button>
                    <Button className="unify-btn-subtle">Subtle Button</Button>
                    <Button disabled className="unify-btn-subtle">Subtle Button Disabled</Button>
                    <Button className="unify-pill">Pill Button</Button>
                    <ActionIcon>
                        <IconSunHigh size={"1.125rem"} />
                    </ActionIcon>
                    <ActionIcon disabled>
                        <IconSunHigh size={"1.125rem"} />
                    </ActionIcon>
                    <ActionIcon className="unify-action-icon-primary">
                        <IconSunHigh size={"1.125rem"} />
                    </ActionIcon>
                    <ActionIcon disabled className="unify-action-icon-primary">
                        <IconSunHigh size={"1.125rem"} />
                    </ActionIcon>
                    <ActionIcon className="unify-action-icon-subtle">
                        <IconSunHigh size={"1.125rem"} />
                    </ActionIcon>
                    <ActionIcon disabled className="unify-action-icon-subtle">
                        <IconSunHigh size={"1.125rem"} />
                    </ActionIcon>
                    <a className="unify-link" href="/">Link</a>
                    <SelectionBox values={["Value1", "Value2"]} />
                </Group>
                :
                <Box style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    <Blockquote cite={author} icon={<IconQuote />} iconSize={16} mt="xl" color="var(--primary-color)" radius="lg">
                        <Text>"{quote}"</Text>
                    </Blockquote>

                    <SnakeGame />
                </Box>
            }
        </BasePage>
    )
}

export default DashboardPage;